import { useEffect } from "react";

/**
 * Keeps a pixel-based <map> aligned with an <img> that scales.
 *
 * <area coords> are absolute pixels measured against one fixed frame, and the
 * browser never rescales them when the image is rendered at any other width.
 * So the moment the banner image becomes fluid the state hotspots drift off
 * the map. This reads the authored coords once, then rewrites them from the
 * image's live rendered width on load, on resize and on orientation change.
 *
 * `baseWidth` is the width the coords were authored against. It defaults to
 * the image's natural width, but must be passed explicitly when the image
 * file is a higher-resolution export of the same artwork (natural size grew,
 * authored coords did not).
 */
function useResponsiveImageMap(imgRef, mapRef, baseWidth) {
    useEffect(() => {
        const img = imgRef.current;
        const map = mapRef.current;
        if (!img || !map) return undefined;

        const areas = Array.from(map.querySelectorAll("area"));
        if (!areas.length) return undefined;

        // Stash the authored coordinates on first run so repeated rescales
        // always start from the original values instead of compounding.
        areas.forEach((area) => {
            if (!area.dataset.baseCoords) {
                area.dataset.baseCoords = area.getAttribute("coords") || "";
            }
        });

        const rescale = () => {
            // Hidden behind a media query (or not decoded yet) - nothing to do.
            if (!img.naturalWidth || !img.clientWidth) return;
            const scale = img.clientWidth / (baseWidth || img.naturalWidth);
            areas.forEach((area) => {
                const base = area.dataset.baseCoords;
                if (!base) return;
                area.setAttribute(
                    "coords",
                    base
                        .split(",")
                        .map((n) => Math.round(parseFloat(n) * scale))
                        .join(",")
                );
            });
        };

        rescale();
        img.addEventListener("load", rescale);
        window.addEventListener("resize", rescale);
        window.addEventListener("orientationchange", rescale);

        const observer =
            typeof ResizeObserver !== "undefined" ? new ResizeObserver(rescale) : null;
        if (observer) observer.observe(img);

        return () => {
            img.removeEventListener("load", rescale);
            window.removeEventListener("resize", rescale);
            window.removeEventListener("orientationchange", rescale);
            if (observer) observer.disconnect();
        };
    }, [imgRef, mapRef, baseWidth]);
}

export default useResponsiveImageMap;
